import { Accordion, Field, useAccordionItemContext } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AsyncStatus } from '@monetize/types/common';
import {
  AccountSettingsConfigSchema,
  ContactSettingsConfigSchema,
  ExperienceConfigurationKeys,
} from '@monetize/types/experience-config';
import { useEffect } from 'react';
import {
  Control,
  Controller,
  UseFormHandleSubmit,
  useForm,
} from 'react-hook-form';
import z from 'zod';
import {
  useGetExperienceConfiguration,
  useUpdateExperienceConfiguration,
} from '../../../api/experienceConfigurationService';
import {
  AsyncStatusIndicator,
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MBox,
  MGrid,
  MRadio,
  MRadioGroup,
  MSkeleton,
  MText,
  MVStack,
} from '../../../components/Monetize';
import { useAccordionControl } from '../../../hooks';
import { useAsyncStatus } from '../../../hooks/useAsyncStatus';
import {
  getHighlightStyles,
  useSettingsSectionHighlight,
} from '../../../hooks/useSettingsSectionHighlight';
import { logger } from '../../../services/logger';
import { TENANT_SETTINGS_SECTIONS } from '../../Settings/SideNavBar/settingsNavSections';

const FormSchema = z.object({
  accountSettings: AccountSettingsConfigSchema,
  contactSettings: ContactSettingsConfigSchema,
});
type Form = z.infer<typeof FormSchema>;

interface AccountAndContactSettingsBodyProps {
  control: Control<any>;
  handleSubmit: UseFormHandleSubmit<Form>;
  onSubmit: (formValues: Form) => Promise<void>;
  accountCreateStatus: ReturnType<typeof useAsyncStatus>;
  accountEditStatus: ReturnType<typeof useAsyncStatus>;
  contactCreateStatus: ReturnType<typeof useAsyncStatus>;
  contactEditStatus: ReturnType<typeof useAsyncStatus>;
}

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const AccountAndContactSettingsBody = ({
  control,
  handleSubmit,
  onSubmit,
  accountCreateStatus,
  accountEditStatus,
  contactCreateStatus,
  contactEditStatus,
}: AccountAndContactSettingsBodyProps) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label={TENANT_SETTINGS_SECTIONS.accountContact.label}
      />
      <Accordion.ItemContent mx={6}>
        <Accordion.ItemBody>
          <MText color="tGray.darkPurple">
            If you want your CRM to be the source of truth for accounts an
            contacts, you can disable the creation or editing of them within
            MonetizeNow.
          </MText>

          <MGrid templateColumns="repeat(2, 1fr)" gap={1} maxW="400px">
            <Controller
              name={`accountSettings.preventAccountCreate`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <EntityCreationEditSelection
                  label="Account Creation"
                  value={!value}
                  asyncStatus={accountCreateStatus.status}
                  onChange={(newValue) => {
                    onChange(!newValue);
                    accountCreateStatus.track(() => handleSubmit(onSubmit)());
                  }}
                />
              )}
            />
            <Controller
              name={`accountSettings.preventAccountEdit`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <EntityCreationEditSelection
                  label="Account Editing"
                  value={!value}
                  asyncStatus={accountEditStatus.status}
                  onChange={(newValue) => {
                    onChange(!newValue);
                    accountEditStatus.track(() => handleSubmit(onSubmit)());
                  }}
                />
              )}
            />
            <Controller
              name={`contactSettings.preventContactCreate`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <EntityCreationEditSelection
                  label="Contact Creation"
                  value={!value}
                  asyncStatus={contactCreateStatus.status}
                  onChange={(newValue) => {
                    onChange(!newValue);
                    contactCreateStatus.track(() => handleSubmit(onSubmit)());
                  }}
                />
              )}
            />
            <Controller
              name={`contactSettings.preventContactEdit`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <EntityCreationEditSelection
                  label="Contact Editing"
                  value={!value}
                  asyncStatus={contactEditStatus.status}
                  onChange={(newValue) => {
                    onChange(!newValue);
                    contactEditStatus.track(() => handleSubmit(onSubmit)());
                  }}
                />
              )}
            />
          </MGrid>
        </Accordion.ItemBody>
      </Accordion.ItemContent>
    </>
  );
};

export const AccountAndContactSettings = () => {
  const { data, isLoading } = useGetExperienceConfiguration();
  const { accordionValue, handleAccordionChange, setAccordionValue } =
    useAccordionControl();
  const accountCreateStatus = useAsyncStatus();
  const accountEditStatus = useAsyncStatus();
  const contactCreateStatus = useAsyncStatus();
  const contactEditStatus = useAsyncStatus();

  const { mutateAsync: doUpdateExperienceConfiguration } =
    useUpdateExperienceConfiguration({
      meta: { showErrorToast: true },
    });

  const { control, reset, handleSubmit } = useForm({
    mode: 'onChange',
    resolver: zodResolver(FormSchema),
    defaultValues: {
      accountSettings: data?.accountSettings || {
        preventAccountCreate: false,
        preventAccountEdit: false,
      },
      contactSettings: data?.contactSettings || {
        preventContactCreate: false,
        preventContactEdit: false,
      },
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        accountSettings: data.accountSettings,
        contactSettings: data.contactSettings,
      });
    }
  }, [data, reset]);

  const onSubmit = async (formValues: Form) => {
    try {
      const promises: Promise<any>[] = [];
      if (
        formValues.accountSettings.preventAccountCreate !==
          data?.accountSettings.preventAccountCreate ||
        formValues.accountSettings.preventAccountEdit !==
          data?.accountSettings.preventAccountEdit
      ) {
        promises.push(
          doUpdateExperienceConfiguration({
            key: ExperienceConfigurationKeys.accountSettings,
            configuration: formValues.accountSettings,
          }),
        );
      }
      if (
        formValues.contactSettings.preventContactCreate !==
          data?.contactSettings.preventContactCreate ||
        formValues.contactSettings.preventContactEdit !==
          data?.contactSettings.preventContactEdit
      ) {
        promises.push(
          doUpdateExperienceConfiguration({
            key: ExperienceConfigurationKeys.contactSettings,
            configuration: formValues.contactSettings,
          }),
        );
      }
      await Promise.all(promises);
    } catch (ex) {
      logger.warn('Error updating experience configuration', ex);
      throw ex;
    }
  };

  const isHighlighted = useSettingsSectionHighlight('accountContact');

  useEffect(() => {
    if (isHighlighted) {
      setAccordionValue(['0']);
    }
  }, [isHighlighted, setAccordionValue]);

  if (isLoading) {
    return (
      <MVStack maxW="600px" gap={4}>
        <MSkeleton height={50} width="100%" />
        <MSkeleton height={50} width="100%" />
        <MSkeleton height={50} width="100%" />
      </MVStack>
    );
  }

  return (
    <MAccordion
      multiple
      w="full"
      value={accordionValue}
      onValueChange={({ value }) => handleAccordionChange(value)}
    >
      <MAccordionItem
        value="0"
        id="accountContact"
        {...getHighlightStyles(isHighlighted)}
        borderWidth="1px"
        borderRadius="md"
        data-testid="default-msa-accordion"
      >
        <AccountAndContactSettingsBody
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          accountCreateStatus={accountCreateStatus}
          accountEditStatus={accountEditStatus}
          contactCreateStatus={contactCreateStatus}
          contactEditStatus={contactEditStatus}
        />
      </MAccordionItem>
    </MAccordion>
  );
};

function EntityCreationEditSelection({
  label,
  value,
  onChange,
  asyncStatus = 'idle',
}: {
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
  asyncStatus?: AsyncStatus;
}) {
  return (
    <Field.Root mt={2}>
      <Field.Label fontSize="md">{label}</Field.Label>
      <MBox
        position="relative"
        display="inline-flex"
        alignItems="center"
        minH="30px"
      >
        <MRadioGroup
          display="flex"
          gap={2}
          pl={2}
          value={value ? 'true' : 'false'}
          isDisabled={asyncStatus === 'loading'}
          onChange={(selected: string) => onChange(selected === 'true')}
        >
          <MRadio value="true">Enabled</MRadio>
          <MRadio value="false">Disabled</MRadio>
        </MRadioGroup>
        <AsyncStatusIndicator status={asyncStatus} />
      </MBox>
    </Field.Root>
  );
}
